<template>
  <el-dialog
    v-model="dialogVisible"
    title="Preview"
    width="90%"
    :before-close="closeDialog"
    destroy-on-close
    class="mt-10 sm:mt-40 lg:mt-16"
  >
    <div v-if="previewFile" class="overflow-y-scroll max-h-[90vh]">
      <template v-if="previewFile.extension">
        <img
          v-if="configImageExtension.includes(previewFile.extension.toLowerCase())"
          :src="previewFile.url"
          alt="preview-image"
          @error="handlePreviewError"
        />
        <SwdPdfViewer
          v-if="previewFile.extension.toLowerCase() === 'pdf' && !previewError"
          type="application/pdf"
          :src="previewFile.url"
          :page="1"
          @error="handlePreviewError"
        />

        <!-- CSV Viewer (Child Component) -->
        <div v-if="csvData.length && previewFile.extension.toLowerCase() === 'csv'">
          <CsvViewer :csv-data="csvData" :column-defs="columnDefs" />
        </div>

        <iframe
          v-if="
            configMicrosoftExtension.includes(previewFile.extension.toLowerCase()) &&
            !['dotm'].includes(previewFile.extension.toLowerCase()) &&
            !previewError
          "
          :src="getSrcMicrosoft"
          width="100%"
          height="500px"
          frameborder="0"
          @error="handlePreviewError"
        ></iframe>

        <!-- Unsupported formats or errors -->
        <div
          v-if="['dotm'].includes(previewFile.extension.toLowerCase()) || previewError || !isSupportedExtension"
          class="p-8 text-center"
        >
          <div class="mb-4">
            <el-icon :size="64" color="#f56c6c">
              <DocumentDelete />
            </el-icon>
          </div>
          <div class="text-lg font-semibold text-gray-700 mb-2">
            {{ previewError ? 'Preview Error' : 'Preview Not Supported' }}
          </div>
          <div class="text-sm text-gray-500 mb-4">
            {{
              previewError
                ? errorMessage || 'Unable to load preview for this file.'
                : `Preview not available for .${previewFile.extension} files.`
            }}
          </div>
          <el-button type="primary" @click="downloadFile">
            <el-icon class="mr-2"><Download /></el-icon>
            Download File
          </el-button>
        </div>
      </template>

      <template v-else>
        <iframe
          v-if="
            configMicrosoftExtension.includes(previewFile?.media?.extension.toLowerCase()) &&
            !['dotm'].includes(previewFile?.media?.extension.toLowerCase()) &&
            !previewError
          "
          :src="getSrcMicrosoft"
          width="100%"
          height="500px"
          frameborder="0"
          @error="handlePreviewError"
        ></iframe>
        <div
          v-if="['dotm'].includes(previewFile?.media?.extension.toLowerCase()) || previewError"
          class="p-8 text-center"
        >
          <div class="mb-4">
            <el-icon :size="64" color="#f56c6c">
              <DocumentDelete />
            </el-icon>
          </div>
          <div class="text-lg font-semibold text-gray-700 mb-2">
            {{ previewError ? 'Preview Error' : 'Preview Not Supported' }}
          </div>
          <div class="text-sm text-gray-500 mb-4">
            {{
              previewError
                ? errorMessage || 'Unable to load preview for this file.'
                : `Preview not available for .${previewFile?.media?.extension} files.`
            }}
          </div>
          <el-button type="primary" @click="downloadFile">
            <el-icon class="mr-2"><Download /></el-icon>
            Download File
          </el-button>
        </div>
      </template>
    </div>

    <template #footer>
      <span class="dialog-footer">
        <div class="flex justify-end">
          <SwdButton class="px-4 py-1" info @click="closeDialog">Close</SwdButton>
        </div>
      </span>
    </template>
  </el-dialog>
</template>

<script>
import { DocumentDelete, Download } from '@element-plus/icons-vue'
import Papa from 'papaparse'
import { ref, watchEffect, computed } from 'vue'
import { useStore } from 'vuex'

import CsvViewer from '@/components/Global/CsvViewer'
import SwdPdfViewer from '@/components/Global/SwdPdfViewer'

export default {
  name: 'SwdPreviewModal',
  components: {
    SwdPdfViewer,
    CsvViewer,
    DocumentDelete,
    Download,
  },
  setup() {
    const store = useStore()
    const dialogVisible = ref(false)
    const previewError = ref(false)
    const errorMessage = ref('')
    const configImageExtension = ['jpeg', 'jpg', 'png']
    const configMicrosoftExtension = ['doc', 'docx', 'xls', 'xlsx', 'xlsm', 'dotx', 'dotm', 'pptx', 'ppt', 'potx']
    const previewFile = computed(() => store.state.globalComponents.previewFile)

    watchEffect(() => {
      dialogVisible.value = store.state.globalComponents.dialog.showDialog.previewModal
      previewError.value = false // Reset error on dialog open
      errorMessage.value = '' // Reset error message on dialog open
    })

    const csvData = ref([])
    const columnDefs = ref([])
    const pageSize = ref(10)

    const isSupportedExtension = computed(() => {
      if (!previewFile.value?.extension) {
        return false
      }
      const ext = previewFile.value.extension.toLowerCase()
      return (
        configImageExtension.includes(ext) ||
        ext === 'pdf' ||
        ext === 'csv' ||
        (configMicrosoftExtension.includes(ext) && !['dotm'].includes(ext))
      )
    })

    const handlePreviewError = (error) => {
      previewError.value = true
      // Capture specific error message if provided
      if (error?.message) {
        errorMessage.value = error.message
      } else if (typeof error === 'string') {
        errorMessage.value = error
      } else {
        errorMessage.value = 'Unable to load preview for this file.'
      }
    }

    const downloadFile = () => {
      const url = previewFile.value?.url || previewFile.value?.media?.url
      if (url) {
        const link = document.createElement('a')
        link.href = url
        link.download = previewFile.value?.name || 'download'
        link.target = '_blank'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    }

    const parseCsvFromBlob = (blob) => {
      if (blob instanceof Blob) {
        const reader = new FileReader()
        reader.onload = (event) => {
          store.commit('globalComponents/setPreviewLoader', false)
          const csvContent = event.target.result

          Papa.parse(csvContent, {
            complete: (result) => {
              handleParseComplete(result)
            },
            header: true,
            skipEmptyLines: true,
            dynamicTyping: true,
            error: (error) => {
              console.error('CSV Parsing Error:', error)
              handlePreviewError()
            },
          })
        }
        reader.onerror = () => {
          handlePreviewError()
        }
        reader.readAsText(blob)
      }
    }

    const handleParseComplete = (result) => {
      if (result.data && result.data.length > 0) {
        columnDefs.value = result.meta.fields.map((field) => ({
          field: field,
        }))

        csvData.value = result.data.map((row) => {
          return Object.keys(row).reduce((acc, key) => {
            acc[key] = row[key] || ''
            return acc
          }, {})
        })
      } else {
        columnDefs.value = result.meta.fields.map((field) => ({
          field: field,
        }))

        csvData.value = Array(20).fill(
          result.meta.fields.reduce((acc, field) => {
            const normalizedKey = field
            acc[normalizedKey] = ''
            return acc
          }, {})
        )
      }
    }

    watchEffect(() => {
      if (previewFile.value && previewFile.value.extension) {
        const ext = previewFile.value.extension.toLowerCase()
        if (ext === 'csv') {
          const file = previewFile.value.url
          if (file instanceof Blob || file instanceof File) {
            parseCsvFromBlob(file)
          }
        }
      }
    })

    const closeDialog = () => {
      dialogVisible.value = false
      store.commit('globalComponents/setShowModal', {
        destination: 'previewModal',
        value: false,
      })
      store.commit('globalComponents/setPreviewFile', null)
      previewError.value = false
      errorMessage.value = ''
    }

    const getSrcMicrosoft = computed(() => {
      store.commit('globalComponents/setPreviewLoader', false)
      const fileURL = previewFile?.value?.url || previewFile?.value?.media?.url
      return 'https://view.officeapps.live.com/op/embed.aspx?src=' + encodeURIComponent(fileURL)
    })

    return {
      closeDialog,
      previewFile,
      csvData,
      columnDefs,
      pageSize,
      dialogVisible,
      configImageExtension,
      configMicrosoftExtension,
      getSrcMicrosoft,
      previewError,
      errorMessage,
      handlePreviewError,
      downloadFile,
      isSupportedExtension,
    }
  },
}
</script>
